import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { formatPrice, formatHour } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import BookingCalendar from "@/components/BookingCalendar";
import BookingsTab from "@/components/owner/BookingsTab";
import CourtsTab from "@/components/owner/CourtsTab";
import FinancialsTab from "@/components/owner/FinancialsTab";
import DiscountsTab from "@/components/owner/DiscountsTab";
import { InstantBookingModal } from "@/components/owner/InstantBookingModal";
import { Plus, Bell, CalendarDays, LayoutDashboard, List, CreditCard, Settings, Edit2, Tag, User, Building2, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { toast } from "sonner";

const NAV_TABS = [
  { key: "dashboard", icon: LayoutDashboard, path: "/owner" },
  { key: "bookings", icon: List, path: "/owner/bookings" },
  { key: "calendar", icon: CalendarDays, path: "/owner/calendar" },
  { key: "courts", icon: Edit2, path: "/owner/courts" },
  { key: "financials", icon: CreditCard, path: "/owner/financials" },
  { key: "discounts", icon: Tag, path: "/owner/discounts" },
  { key: "settings", icon: Settings, path: "/owner/settings" },
];

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function OwnerDashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const pathTab = location.pathname.replace("/owner/", "") || "dashboard";
  const activeTab = NAV_TABS.find((tab) => tab.key === pathTab) ? pathTab : "dashboard";

  const navRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const instantBookingOpen = searchParams.get("newBooking") === "1";

  const updateScrollState = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    const tolerance = 2;
    setCanScrollLeft(el.scrollLeft > tolerance);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = navRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateScrollState]);

  const scrollNav = (dir) => {
    navRef.current?.scrollBy({ left: dir === "left" ? -200 : 200, behavior: "smooth" });
  };

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const btn = el.querySelector(`[data-tab="${activeTab}"]`);
    if (btn) {
      btn.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [activeTab]);

  const [courts, setCourts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [earnings, setEarnings] = useState({ total: 0, count: 0, series: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, b, e] = await Promise.all([
        api.get("/owner/courts"),
        api.get("/owner/bookings"),
        api.get("/owner/earnings"),
      ]);
      setCourts(c.data);
      setBookings(b.data);
      setEarnings(e.data);
    } catch (err) {
      setError(err.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const today = todayStr();

  const pendingApproval = bookings.filter((b) => b.status === "pending");
  const pendingPayments = bookings.filter(
    (b) => (b.payment_status === "pending_cod" && b.status !== "cancelled" && b.status !== "rejected") || b.payment_status === "cliQ_pending"
  );
  const confirmedBookings = bookings.filter((b) => b.status === "confirmed");
  const todayEarnings = confirmedBookings
    .filter((b) => b.date === today)
    .reduce((sum, b) => sum + (b.total_price || 0), 0);
  const monthEarnings = confirmedBookings
    .filter((b) => b.date.startsWith(today.slice(0, 7)))
    .reduce((sum, b) => sum + (b.total_price || 0), 0);
  const todayBookingsCount = confirmedBookings.filter((b) => b.date === today).length;
  const monthBookingsCount = confirmedBookings.filter((b) => b.date.startsWith(today.slice(0, 7))).length;

  const yesterday = (() => { const d = new Date(); d.setDate(d.getDate() - 1); const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0"); return `${y}-${m}-${day}`; })();
  const yesterdayEarnings = confirmedBookings
    .filter((b) => b.date === yesterday)
    .reduce((sum, b) => sum + (b.total_price || 0), 0);
  const yesterdayBookingsCount = confirmedBookings.filter((b) => b.date === yesterday).length;

  const prevMonthStr = (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); })();
  const prevMonthEarnings = confirmedBookings
    .filter((b) => b.date.startsWith(prevMonthStr))
    .reduce((sum, b) => sum + (b.total_price || 0), 0);
  const prevMonthBookingsCount = confirmedBookings.filter((b) => b.date.startsWith(prevMonthStr)).length;

  const pct = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const trendBadge = (current, previous) => {
    const v = pct(current, previous);
    if (v === 0) return null;
    return <div className={`text-[9px] font-bold mt-0.5 ${v > 0 ? "text-emerald-400" : "text-red-400"}`}>{v > 0 ? "↑" : "↓"} {Math.abs(v)}%</div>;
  };

  const upcomingSchedule = bookings
    .filter((b) => b.status === "confirmed" && b.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date) || a.start_hour - b.start_hour)
    .slice(0, 20)
    .reduce((groups, b) => {
      if (!groups.length || groups[groups.length - 1].date !== b.date) {
        groups.push({ date: b.date, bookings: [] });
      }
      groups[groups.length - 1].bookings.push(b);
      return groups;
    }, []);

  const now = new Date();
  const currentHour = now.getHours() + now.getMinutes() / 60;

  const liveNowBookings = confirmedBookings
    .filter((b) => b.date === today && b.start_hour <= currentHour && b.end_hour > currentHour)
    .sort((a, b) => a.start_hour - b.start_hour);

  const upcoming2hBookings = confirmedBookings
    .filter((b) => b.date === today && b.start_hour > currentHour && b.start_hour <= currentHour + 2)
    .sort((a, b) => a.start_hour - b.start_hour);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="label-eyebrow text-volt mb-2">{t("owner.eyebrow")}</div>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight">{t("owner.title", { name: user?.name || "" })}</h1>
          </div>
          <button
            onClick={() => setSearchParams((prev) => { prev.set("newBooking", "1"); return prev; })}
            className="flex items-center gap-2 bg-volt text-black font-bold text-xs tracking-widest uppercase px-5 py-3 rounded-sm hover:bg-volt/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t("owner.new_booking")}
          </button>
        </div>

        <div className="relative mb-8">
          {canScrollLeft && (
            <button
              onClick={() => scrollNav("left")}
              className="absolute left-0 top-0 bottom-0 z-10 flex items-center bg-gradient-to-r from-background from-50% to-transparent pr-4 md:hidden"
              aria-label="Scroll tabs left"
            >
              <ChevronLeft className="h-4 w-4 text-muted-foreground/70 hover:text-foreground transition-colors" />
            </button>
          )}
          <div
            ref={navRef}
            onScroll={updateScrollState}
            className="flex border-b border-border overflow-x-auto gap-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            {NAV_TABS.map(({ key, icon: Icon, path }) => (
              <button
                key={key}
                data-tab={key}
                onClick={() => navigate(path)}
                className={`flex items-center gap-2 px-4 py-3 text-[11px] tracking-widest uppercase font-bold whitespace-nowrap border-b-2 transition-all ${
                  activeTab === key
                    ? "border-volt text-volt bg-volt/10"
                    : "border-transparent text-muted-foreground/80 hover:text-foreground/80"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t("owner.nav_" + key)}
              </button>
            ))}
          </div>
          {canScrollRight && (
            <button
              onClick={() => scrollNav("right")}
              className="absolute right-0 top-0 bottom-0 z-10 flex items-center bg-gradient-to-l from-background from-50% to-transparent pl-4 md:hidden"
              aria-label="Scroll tabs right"
            >
              <ChevronRight className="h-4 w-4 text-muted-foreground/70 hover:text-foreground transition-colors" />
            </button>
          )}
        </div>

        {/* ──────── DASHBOARD ──────── */}
        {activeTab === "dashboard" && (
          <>
            {loading ? (
              <div className="space-y-8 animate-pulse">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mb-8">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="border border-border bg-card p-5 space-y-3">
                      <div className="h-3 bg-muted w-1/3" />
                      <div className="h-8 bg-muted w-2/3" />
                    </div>
                  ))}
                </div>
                <div className="grid lg:grid-cols-[1fr_380px] gap-6 mb-8">
                  <div className="border border-border bg-card p-5 space-y-4">
                    <div className="h-3 bg-muted w-1/4" />
                    <div className="h-48 bg-muted rounded" />
                  </div>
                  <div className="border border-border bg-card p-5 space-y-4">
                    <div className="h-3 bg-muted w-1/3" />
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-16 bg-muted rounded" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : error ? (
              <div className="border border-red-400/20 bg-red-400/5 p-10 text-center mb-8">
                <p className="text-red-400 font-heading text-xl mb-2">{t("owner.dashboard_error")}</p>
                <p className="text-sm text-muted-foreground/80 mb-4">{error}</p>
                <button onClick={loadAll} className="btn-volt rounded-sm px-5 py-2.5 text-xs">
                  {t("owner.retry")}
                </button>
              </div>
            ) : (
              <>
                {(pendingApproval.length > 0 || pendingPayments.length > 0) && (
                  <div className="border border-yellow-400/20 bg-yellow-400/5 p-5 mb-8">
                    <div className="flex items-center gap-2 mb-3">
                      <Bell className="h-4 w-4 text-yellow-400" />
                      <span className="text-[10px] tracking-widest uppercase text-yellow-400 font-bold">{t("owner.pending_actions")}</span>
                    </div>
                    <div className="flex flex-wrap gap-4">
                      {pendingApproval.length > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-foreground/80">{pendingApproval.length}x</span>
                          <span className="text-muted-foreground">{t("owner.pending_approval")}</span>
                          <button
                            onClick={() => navigate("/owner/bookings?status=pending")}
                            className="text-[10px] tracking-widest uppercase text-volt hover:underline"
                          >
                            {t("owner.view_all")}
                          </button>
                        </div>
                      )}
                      {pendingPayments.length > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-foreground/80">{pendingPayments.length}x</span>
                          <span className="text-muted-foreground">{t("owner.pending_payment")}</span>
                          <button
                            onClick={() => navigate("/owner/bookings")}
                            className="text-[10px] tracking-widest uppercase text-volt hover:underline"
                          >
                            {t("owner.view_all")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mb-8">
                  <div onClick={() => navigate("/owner/bookings")} className="border border-border bg-card p-5 cursor-pointer hover:border-volt/30 transition-colors">
                    <div className="label-eyebrow mb-3">{t("owner.revenue_title")}</div>
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <div className="font-heading text-3xl md:text-4xl text-volt">{formatPrice(todayEarnings, "JOD")}</div>
                        <div className="text-[10px] text-muted-foreground/80 tracking-wider uppercase mt-1">{t("owner.today_label")}</div>
                        {trendBadge(todayEarnings, yesterdayEarnings)}
                      </div>
                      <div className="w-px self-stretch bg-border/30" />
                      <div className="flex-1">
                        <div className="font-heading text-3xl md:text-4xl text-volt">{formatPrice(monthEarnings, "JOD")}</div>
                        <div className="text-[10px] text-muted-foreground/80 tracking-wider uppercase mt-1">{t("owner.month_label")}</div>
                        {trendBadge(monthEarnings, prevMonthEarnings)}
                      </div>
                    </div>
                  </div>
                  <div onClick={() => navigate("/owner/bookings")} className="border border-border bg-card p-5 cursor-pointer hover:border-volt/30 transition-colors">
                    <div className="label-eyebrow mb-3">{t("owner.bookings_title")}</div>
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <div className="font-heading text-3xl md:text-4xl">{todayBookingsCount}</div>
                        <div className="text-[10px] text-muted-foreground/80 tracking-wider uppercase mt-1">{t("owner.today_label")}</div>
                        {trendBadge(todayBookingsCount, yesterdayBookingsCount)}
                      </div>
                      <div className="w-px self-stretch bg-border/30" />
                      <div className="flex-1">
                        <div className="font-heading text-3xl md:text-4xl">{monthBookingsCount}</div>
                        <div className="text-[10px] text-muted-foreground/80 tracking-wider uppercase mt-1">{t("owner.month_label")}</div>
                        {trendBadge(monthBookingsCount, prevMonthBookingsCount)}
                      </div>
                    </div>
                  </div>
                  <div onClick={() => navigate("/owner/courts")} className="border border-border bg-card p-5 cursor-pointer hover:border-volt/30 transition-colors">
                    <div className="label-eyebrow mb-3">{t("owner.active_courts")}</div>
                    <div className="font-heading text-3xl md:text-4xl">{courts.filter((c) => c.active).length}</div>
                  </div>
                  <div onClick={() => navigate("/owner/bookings?status=pending")} className="border border-border bg-card p-5 cursor-pointer hover:border-volt/30 transition-colors">
                    <div className="label-eyebrow mb-3">{t("owner.pending_count")}</div>
                    <div className="font-heading text-3xl md:text-4xl text-yellow-400">{pendingApproval.length}</div>
                  </div>
                </div>

                {(liveNowBookings.length > 0 || upcoming2hBookings.length > 0) && (
                  <div className="border border-border bg-card p-5 mb-8">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="h-4 w-4 text-volt" />
                      <span className="label-eyebrow">{t("owner.live_now_section")}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {liveNowBookings.map((b) => (
                        <div
                          key={b.id}
                          onClick={() => navigate("/owner/bookings")}
                          className="border border-emerald-400/30 bg-emerald-400/5 p-3 flex-1 min-w-[180px] cursor-pointer hover:bg-emerald-400/10 transition-colors"
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                            </span>
                            <span className="text-[9px] tracking-widest uppercase text-emerald-400 font-bold">{t("owner.live")}</span>
                          </div>
                          <div className="text-xs font-semibold truncate">{b.court_name}</div>
                          <div className="text-[10px] text-muted-foreground/80 truncate">{b.user_name}</div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] font-mono text-muted-foreground">{formatHour(b.start_hour)}–{formatHour(b.end_hour)}</span>
                            <span className="text-xs text-volt font-bold">{formatPrice(b.total_price, b.currency)}</span>
                          </div>
                        </div>
                      ))}
                      {upcoming2hBookings.map((b) => (
                        <div
                          key={b.id}
                          onClick={() => navigate("/owner/bookings")}
                          className="border border-yellow-400/30 bg-yellow-400/5 p-3 flex-1 min-w-[180px] cursor-pointer hover:bg-yellow-400/10 transition-colors"
                        >
                          <div className="text-[9px] tracking-widest uppercase text-yellow-400 font-bold mb-1">{t("owner.soon")}</div>
                          <div className="text-xs font-semibold truncate">{b.court_name}</div>
                          <div className="text-[10px] text-muted-foreground/80 truncate">{b.user_name}</div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] font-mono text-muted-foreground">{formatHour(b.start_hour)}</span>
                            <span className="text-xs text-volt font-bold">{formatPrice(b.total_price, b.currency)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {courts.length === 0 ? (
                  <div className="border border-dashed border-border p-16 text-center space-y-4 mb-8">
                    <Building2 className="h-12 w-12 text-muted-foreground/40 mx-auto" />
                    <h2 className="font-heading text-2xl text-foreground/80">{t("owner.first_court_title")}</h2>
                    <p className="text-sm text-muted-foreground/80 max-w-md mx-auto">{t("owner.first_court_desc")}</p>
                    <button
                      onClick={() => navigate("/owner/courts/new")}
                      className="btn-volt rounded-sm px-5 py-3 text-xs flex items-center gap-2 mx-auto"
                    >
                      <Plus className="h-4 w-4" /> {t("owner.add_first_court")}
                    </button>
                  </div>
                ) : (
                  <div className="grid lg:grid-cols-[1fr_380px] gap-6 mb-8">
              <div className="border border-border bg-card p-5">
                <div className="label-eyebrow mb-4">{t("owner.earnings_trend")}</div>
                <div className="h-56">
                  {earnings.series.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-muted-foreground/60 text-sm">
                      {t("owner.no_data")}
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={earnings.series}>
                        <CartesianGrid stroke="#27272A" strokeDasharray="2 4" />
                        <XAxis dataKey="date" stroke="#71717A" fontSize={10} />
                        <YAxis stroke="#71717A" fontSize={10} />
                        <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A" }} />
                        <Line type="monotone" dataKey="amount" stroke="#CCFF00" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-4">
                  <CalendarDays className="h-4 w-4 text-volt" />
                  <span className="label-eyebrow">{t("owner.upcoming_schedule")}</span>
                </div>
                <div className="space-y-1 max-h-80 overflow-y-auto">
                  {upcomingSchedule.length === 0 ? (
                    <div className="text-sm text-muted-foreground/60 flex items-center justify-center h-40">
                      {t("owner.no_bookings_today")}
                    </div>
                  ) : upcomingSchedule.map(({ date, bookings: dayBookings }) => {
                    const dateObj = new Date(date + "T00:00:00");
                    const dayName = dateObj.toLocaleDateString(i18n.language?.startsWith("ar") ? "ar-EG" : "en-US", { weekday: "short" });
                    const isToday = date === today;
                    return (
                      <div key={date} className="border border-border bg-background p-3">
                        <div className={`text-[10px] tracking-widest uppercase font-bold mb-1 ${isToday ? "text-volt" : "text-muted-foreground/80"}`}>
                          {isToday ? t("owner.today_label") : dayName} <span className="text-muted-foreground/60 font-normal">{date}</span>
                        </div>
                        <div className="space-y-1">
                          {dayBookings.map((b) => (
                            <div
                              key={b.id}
                              onClick={() => navigate("/owner/bookings")}
                              className="flex items-center gap-2 cursor-pointer hover:bg-muted p-1.5 rounded-sm transition-colors"
                            >
                              <div className="text-[9px] font-mono text-yellow-400/70 flex-shrink-0 w-14 text-end">
                                {formatHour(b.start_hour)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-semibold truncate">{b.court_name}</div>
                                <div className="text-[10px] text-muted-foreground/80 truncate">{b.user_name}</div>
                              </div>
                              <div className="text-xs text-volt font-bold flex-shrink-0">
                                {formatPrice(b.total_price, b.currency)}
                              </div>
                              {b.selected_extras && b.selected_extras.length > 0 && (
                                <div className="text-[8px] tracking-widest uppercase text-yellow-400/70 font-bold flex-shrink-0 border border-yellow-400/30 px-1 py-0.5 leading-none">
                                  +{b.selected_extras.length}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
                )}
              </>
            )}
          </>
        )}

        {/* ──────── BOOKINGS ──────── */}
        {activeTab === "bookings" && (
          <BookingsTab bookings={bookings} onRefresh={loadAll} />
        )}

        {/* ──────── CALENDAR ──────── */}
        {activeTab === "calendar" && (
          <BookingCalendar onRefresh={loadAll} />
        )}

        {/* ──────── COURTS ──────── */}
        {activeTab === "courts" && (
          <CourtsTab courts={courts} bookings={bookings} onRefresh={loadAll} />
        )}

        {/* ──────── FINANCIALS ──────── */}
        {activeTab === "financials" && (
          <FinancialsTab bookings={bookings} earnings={earnings} />
        )}

        {/* ──────── DISCOUNTS ──────── */}
        {activeTab === "discounts" && (
          <DiscountsTab courts={courts} />
        )}

        {/* ──────── SETTINGS ──────── */}
        {activeTab === "settings" && (
          <div className="max-w-[600px] space-y-4">
            <div className="border border-border bg-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <User className="h-5 w-5 text-volt" />
                <span className="label-eyebrow">{t("owner.profile")}</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("owner.name")}</span>
                  <span>{user?.name || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("owner.email")}</span>
                  <span>{user?.email || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("owner.role")}</span>
                  <span className="uppercase text-[10px] tracking-widest text-volt">{user?.role}</span>
                </div>
              </div>
            </div>
            <div className="border border-border bg-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="h-5 w-5 text-volt" />
                <span className="label-eyebrow">{t("owner.payout_settings")}</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{t("owner.payout_settings_desc")}</p>
            </div>
          </div>
        )}
      </div>

      {/* ──────── INSTANT MANUAL BOOKING MODAL ──────── */}
      {instantBookingOpen && (
        <InstantBookingModal
          courts={courts}
          onClose={() => setSearchParams((prev) => { prev.delete("newBooking"); return prev; })}
          onCreated={(booking) => {
            setSearchParams((prev) => { prev.delete("newBooking"); return prev; });
            loadAll();
            toast.success(t("owner.booking_created"));
            navigate(`/owner/bookings?highlight=${booking.id}`);
          }}
        />
      )}
    </div>
  );
}

